import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import logger from '@/lib/logger';

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const { user } = useAuth();
  const [completedTours, setCompletedTours] = useState([]);
  const [activeTour, setActiveTour] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) { setLoaded(true); return; }
    api.getCompletedTours()
      .then(r => setCompletedTours(r.data.completed_tours || []))
      .catch(err => logger.error(err))
      .finally(() => setLoaded(true));
  }, [user]);

  const startTour = useCallback((tourId, steps) => {
    if (!user || completedTours.includes(tourId)) return false;
    setActiveTour({ id: tourId, steps });
    setActiveStep(0);
    return true;
  }, [user, completedTours]);

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    if (activeStep < activeTour.steps.length - 1) {
      setActiveStep(prev => prev + 1);
    } else {
      // Tour complete
      api.completeTour(activeTour.id).catch(err => logger.error(err));
      setCompletedTours(prev => [...prev, activeTour.id]);
      setActiveTour(null);
      setActiveStep(0);
    }
  }, [activeTour, activeStep]);

  const skipTour = useCallback(() => {
    if (!activeTour) return;
    api.completeTour(activeTour.id).catch(err => logger.error(err));
    setCompletedTours(prev => [...prev, activeTour.id]);
    setActiveTour(null);
    setActiveStep(0);
  }, [activeTour]);

  const resetAllTours = useCallback(async () => {
    await api.resetTours();
    setCompletedTours([]);
  }, []);

  const isTourCompleted = useCallback((tourId) => completedTours.includes(tourId), [completedTours]);

  const contextValue = useMemo(() => ({
    activeTour, activeStep, loaded, completedTours,
    startTour, nextStep, skipTour, resetAllTours, isTourCompleted,
  }), [activeTour, activeStep, loaded, completedTours, startTour, nextStep, skipTour, resetAllTours, isTourCompleted]);

  return (
    <TourContext.Provider value={contextValue}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be inside TourProvider');
  return ctx;
}
